import { memo, type CSSProperties } from "react";
import type { InflationBreakdown, OriginBreakdown } from "../../shared/types";
import { formatCompactFlr, formatCoverageBps, formatFlr } from "../../shared/format";

const destinationPalette = ["#9B5CFF", "#FF338F", "#D64CFF", "#FFC066", "#55E0BC", "#FF89C4"];

export function BurnOrigins({ origins }: { origins: OriginBreakdown[] }) {
  return (
    <section className="breakdown-panel" aria-labelledby="origins-title">
      <div className="section-heading section-heading--compact">
        <div><span className="eyebrow">Fee activity · direct burns separated</span><h2 id="origins-title">Burn origins</h2></div>
        <span className="coverage-key"><i /> Receipt-derived and on-chain exact</span>
      </div>
      <div className="breakdown-list">
        {origins.length ? origins.map((origin, index) => (
          <div className="breakdown-row" key={origin.family}>
            <span className="breakdown-row__rank">{String(index + 1).padStart(2, "0")}</span>
            <div className="breakdown-row__label"><b>{origin.label}</b><small>{origin.confidence} classification</small></div>
            <div className="breakdown-row__bar"><i style={{ width: `${origin.shareBps / 100}%` }} /></div>
            <strong>{formatFlr(origin.amountWei)} <small>FLR</small></strong>
            <span>{formatCoverageBps(String(origin.shareBps))}</span>
          </div>
        )) : <p className="empty-state">No matching activity occurred in this window.</p>}
      </div>
    </section>
  );
}

export const InflationDestinations=memo(function InflationDestinations({ rows }: { rows: InflationBreakdown[] }) {
  const total = rows.reduce((sum, row) => sum + BigInt(row.authorizedWei), 0n);
  const totalMinted = rows.reduce((sum, row) => sum + BigInt(row.mintedWei), 0n);
  const destinations = [...rows]
    .map((row) => ({ row, shareBps: total ? (BigInt(row.authorizedWei) * 10_000n) / total : 0n }))
    .sort((a, b) => Number(b.shareBps - a.shareBps));
  const segments = destinations.flatMap(({ shareBps }, index) => {
    const share = Number(shareBps) / 100;
    if (!share) return [];
    const start = destinations.slice(0, index).reduce((sum, item) => sum + Number(item.shareBps) / 100, 0);
    const end = Math.min(100, start + share);
    const seam = Math.max(start, end - Math.min(.55, share / 3));
    return [`${destinationPalette[index % destinationPalette.length]} ${start}% ${seam}%`, `rgba(6,3,16,.94) ${seam}% ${end}%`];
  });
  const wheelStyle = { background: segments.length ? `conic-gradient(from -90deg, ${segments.join(", ")})` : "var(--grid)" };

  return (
    <section className="breakdown-panel inflation-panel" aria-labelledby="inflation-title">
      <div className="section-heading section-heading--compact">
        <div><span className="eyebrow">Authorized allocation · realized minting</span><h2 id="inflation-title">Inflation Destinations</h2></div>
        {!!rows.length && <div className="inflation-panel__summary"><span>Minted in this window</span><strong>{formatCompactFlr(totalMinted)} FLR</strong></div>}
      </div>
      {rows.length ? <div className="inflation-map">
        <div className="inflation-wheel-wrap">
          <div className="inflation-wheel__ticks" aria-hidden="true" />
          <div className="inflation-wheel" style={wheelStyle} role="img" aria-label={`Authorized inflation split across ${rows.length} destinations`}>
            <div className="inflation-wheel__core">
              <span>Total mandate</span>
              <strong>{formatCompactFlr(total)}</strong>
              <small>FLR authorized</small>
            </div>
          </div>
          <div className="inflation-wheel__note"><span>Largest allocation</span><strong>{destinations[0]?.row.label}</strong></div>
        </div>

        <div className="destination-list">
          {destinations.map(({ row, shareBps }, index) => {
            const color = destinationPalette[index % destinationPalette.length];
            const share = Number(shareBps) / 100;
            const style = { "--destination-color": color, "--destination-share": `${share}%` } as CSSProperties;
            return (
              <div className="destination-row" key={row.receiver} style={style}>
                <span className="destination-row__rank">{String(index + 1).padStart(2, "0")}</span>
                <i className="destination-row__key" aria-hidden="true" />
                <div className="destination-row__label"><b>{row.label}</b><small>{row.confidence} attribution</small></div>
                <div className="destination-row__track" aria-hidden="true"><i /></div>
                <strong>{formatFlr(row.mintedWei)} <small>FLR minted</small></strong>
                <span className="destination-row__share">{formatCoverageBps(shareBps.toString())}<small>authorized</small></span>
              </div>
            );
          })}
        </div>
      </div> : <p className="empty-state">Inflation receiver attribution is not available for this window.</p>}
    </section>
  );
});
